import {
  AppError,
  AuthnViews,
  ErrorCode,
  type EventPayload,
  EventType,
  FatalError,
  isFactorLockError,
} from '@imprivata-cloud/authn';
import { createSpanName, debug, SpanComponent } from '@imprivata-cloud/common';
import type { FaceCeremony } from '@imprivata-cloud/core';
import { useCallback, useRef } from 'react';

import { cameraEventOutcome } from '@/errorHandler/camera';

import type { BannerContext } from './useBanner';

const ERROR_SCREEN_CODES = new Set<ErrorCode>([
  ErrorCode.UNSUPPORTED_INLINE_ENROLLMENT,
  ErrorCode.ACCOUNT_DISABLED,
  ErrorCode.ACCOUNT_LOCKED,
  ErrorCode.UNSUPPORTED_OPERATION,
  ErrorCode.CREDENTIAL_EXPIRED,
]);

function toAppError(
  data: AppError | string,
  fallbackCode: ErrorCode = ErrorCode.UNKNOWN,
): AppError {
  return typeof data === 'string'
    ? new AppError({ message: data, code: fallbackCode })
    : data;
}

// Raw backend wire code returned by authn-session/start when the username
// cannot be resolved. It has no ErrorCode enum member (the enum's NOT_FOUND is
// the lowercase EPCS "not-found"), and the module's 5xx interceptor escalates
// it to a FatalError.
const BACKEND_USER_NOT_FOUND_CODE = 'NOT_FOUND';

// Username-resolution failures the user recovers from by re-entering their
// username. authn-session/start answers these with HTTP 500, so the authn
// module's 5xx interceptor escalates them to FatalError — which would otherwise
// redirect to the relying party instead of showing a correctable banner.
const RECOVERABLE_USERNAME_ERROR_CODES = new Set<string>([
  ErrorCode.AMBIGUOUS_USERNAME,
  ErrorCode.NOT_FOUND,
  BACKEND_USER_NOT_FOUND_CODE,
]);

function isRecoverableUsernameError(error: AppError): boolean {
  return (
    error.code !== undefined && RECOVERABLE_USERNAME_ERROR_CODES.has(error.code)
  );
}

// The attempted username the authn module carries on the error's metadata bag
// (`details.data.username`, the same bag `getUserUpnFromError` reads `user-upn`
// from). Echoed back to the relying party as `login_hint` on the failure redirect
// so it can pre-fill its login form on retry (undefined in 1:N flows, where no
// username was entered).
export function loginHintOf(error: AppError): string | undefined {
  const username = error.details?.data?.username;
  return typeof username === 'string' ? username : undefined;
}

// Rebuild a (possibly Fatal) error as a plain, banner-eligible AppError so the
// shared banner path surfaces it — useBanner suppresses FatalError instances.
function toRecoverableError(error: AppError): AppError {
  return new AppError(
    { code: error.code ?? ErrorCode.UNKNOWN, message: error.message },
    error.params,
  );
}

interface UseEventHandlerDeps {
  // Whether this is a 1:N (identify) flow, where no username was entered. Such
  // flows cannot recover a username-resolution failure by returning to the
  // UserNameForm, so those errors must redirect to the RP instead of bannering.
  isOneToMany: boolean;
  // Control flow: terminal/redirect transitions of the top-level flow.
  onSuccess: (opts: { authorizationCode: string }) => void | Promise<void>;
  onFailure: (options: {
    redirectDelayMs?: number;
    errorCode?: string;
    errorDescription?: string;
    loginHint?: string;
  }) => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  setUsername: (username: string | undefined) => void;
  // Orthogonal side-channels: the transient notification banner and error screen.
  // Camera copy names what the user was trying to do ("to enroll your face" vs
  // "for face authentication"), so the banner takes the ceremony the failure
  // interrupted; this hook reads it off the enroll transitions it already sees.
  displayBanner: (err: AppError, context: BannerContext) => void;
  clearBanner: () => void;
  setScreenError: (error: AppError | undefined) => void;
  // View configuration: a single transition of the unified presentation state.
  dispatchView: (payload: EventPayload) => void;
}

export function useEventHandler({
  isOneToMany,
  onSuccess,
  onFailure,
  onCancel,
  setUsername,
  displayBanner,
  clearBanner,
  setScreenError,
  dispatchView,
}: UseEventHandlerDeps) {
  // Which ceremony is on screen, tracked in a ref rather than read from the
  // container's ViewState: this hook's callback is passed to <Authn> as `onEvent`,
  // and authn memoizes its whole prop bag on it — re-creating its api service, and
  // with it the store holding the mid-ceremony session, whenever the identity
  // changes. START_ENROLLING is the only signal for the enroll gate (its intro and
  // consent screens emit no VIEW_CHANGED), and it is one of the events handled
  // here, so the ceremony is derivable without adding a dependency.
  const enrollRef = useRef(false);

  // Whether the face-capture screen is on view, tracked the same way and for the
  // same reason as the ceremony above: the banner is repositioned over the video
  // there, but reading it off the container's ViewState would put `view` in this
  // callback's deps and churn its identity on every VIEW_CHANGED. The screen is
  // set solely by VIEW_CHANGED (see reduceView), so this ref mirrors it exactly.
  const faceRef = useRef(false);

  const handleEvent = useCallback(
    async (payload: EventPayload) => {
      const { event, data } = payload;
      debug('Received event', { event, data });
      createSpanName(SpanComponent.UI, 'event', `on-event--${event}`);

      if (event === EventType.START_ENROLLING) {
        enrollRef.current = !!data;
      }
      if (event === EventType.VIEW_CHANGED) {
        faceRef.current = data === AuthnViews.FACE_ID;
      }
      // Nothing else distinguishes the two ceremonies, so the absence of the
      // enroll gate is authentication.
      const ceremony: FaceCeremony = enrollRef.current ? 'enroll' : 'auth';

      if (event === EventType.AUTHENTICATED) {
        debug('Authentication successful');
        await onSuccess({
          authorizationCode: data?.oidcAuthorizationCode ?? '',
        });
        return;
      }

      if (event === EventType.CANCEL) {
        debug('Authentication canceled by user');
        await onCancel();
        return;
      }

      if (event === EventType.ERROR) {
        // A username-resolution failure from authn-session/start (e.g. the raw
        // "NOT_FOUND" at HTTP 500) is escalated to FatalError by the module's
        // 5xx interceptor. Demote it: clear the username to return to the
        // UserNameForm and show a banner instead of redirecting to the RP.
        // 1:N flows have no username to re-enter, so they skip this and fall
        // through to the FatalError redirect below.
        if (
          !isOneToMany &&
          data instanceof FatalError &&
          isRecoverableUsernameError(data)
        ) {
          setUsername(undefined);
          displayBanner(toRecoverableError(data), {
            ceremony,
            isFaceScreen: faceRef.current,
          });
          return;
        }

        if (data instanceof FatalError) {
          return await onFailure({
            errorCode: data.code,
            errorDescription: data.message,
            loginHint: loginHintOf(data),
          });
        }

        if (data instanceof AppError) {
          if (
            data.code === ErrorCode.AMBIGUOUS_USERNAME ||
            data.code === ErrorCode.USER_NOT_FOUND
          ) {
            setUsername(undefined);
          }

          if (ERROR_SCREEN_CODES.has(data.code as ErrorCode)) {
            clearBanner();
            setScreenError(data);
            return;
          }

          if (isFactorLockError(data)) {
            clearBanner();
            return;
          }

          if (data.code === ErrorCode.FACE_TIMEOUT) {
            await onFailure({
              errorCode: data.code,
              errorDescription: data.message,
              loginHint: loginHintOf(data),
            });
            clearBanner();
            return;
          }

          if (
            data.code ===
              ErrorCode.GENERIC_FACE_AUTHENTICATION_ERROR_WITHOUT_BANNER ||
            data.code === ErrorCode.LIVENESS_FAILED ||
            data.code === ErrorCode.FACTOR_CAPTURE_FAILED
          ) {
            clearBanner();
            return;
          }

          displayBanner(data, { ceremony, isFaceScreen: faceRef.current });
          return;
        }
      }

      switch (event) {
        case EventType.CLEAR_BANNER:
        case EventType.RESTART_STATE:
        // Reset the in-place flow (enroll/account-recovery) by clearing the
        // transient banner and screen error.
        case EventType.INTERNAL_RESTART:
          clearBanner();
          setScreenError(undefined);
          break;

        case EventType.IMPR_ID_ERROR:
        case EventType.INTERNAL_ERROR:
          if (data) {
            if (data instanceof FatalError) {
              void onFailure({
                errorCode: data.code,
                errorDescription: data.message,
                loginHint: loginHintOf(data),
              });
            }
            displayBanner(toAppError(data), {
              ceremony,
              isFaceScreen: faceRef.current,
            });
          }
          break;

        // Camera availability is decided by the shared mapping so this hook and
        // tenant-lookup cannot drift on which codes reach the user.
        case EventType.CAMERA_ERROR:
        case EventType.CAMERA_IN_USE:
        case EventType.PERMISSION_REVOKED:
        case EventType.DEVICE_DISCONNECTED:
        case EventType.PERMISSION_GRANTED:
        case EventType.DEVICE_RECONNECTED: {
          const outcome = cameraEventOutcome(event, data);
          switch (outcome.kind) {
            case 'banner':
              displayBanner(
                data instanceof AppError
                  ? data
                  : new AppError({ code: outcome.code }),
                { ceremony, isFaceScreen: faceRef.current },
              );
              break;
            case 'terminal':
              return await onFailure({
                errorCode: outcome.code,
                errorDescription:
                  data instanceof AppError ? data.message : undefined,
                loginHint:
                  data instanceof AppError ? loginHintOf(data) : undefined,
              });
            case 'clear':
              clearBanner();
              break;
            case 'ignore':
              break;
          }
          break;
        }

        // View configuration: the header, hex glyph and layout flags are pure
        // projections of one presentation state, so these only transition it.
        // Entering the face-capture screen (VIEW_CHANGED -> FACE_ID) retires the
        // start-enroll hero for the rest of the enroll flow (see reduceView).
        case EventType.START_ENROLLING:
        case EventType.START_RECONSENT:
        case EventType.START_AUTHENTICATING:
        case EventType.FINISH_AUTHENTICATING:
        case EventType.VIEW_CHANGED:
          clearBanner();
          dispatchView(payload);
          break;

        default:
          break;
      }
    },
    [
      isOneToMany,
      onSuccess,
      onFailure,
      onCancel,
      setUsername,
      displayBanner,
      clearBanner,
      setScreenError,
      dispatchView,
    ],
  );

  return handleEvent;
}
